/* ─────────────────────────────────────────────────────────────────────────────
   LEADS — atribución del visitante y envío al CRM (INI-145).

   ESM sin imports de Next: lo usan el formulario de /contacto, el newsletter y
   las pruebas (tests/leads.test.mjs). Las funciones puras (parsear, mezclar,
   armar el payload) no tocan el navegador; las que sí, lo hacen con try/catch.

   Contrato del CRM (bison-crm): POST JSON solo desde https://bisonagents.com (CORS
   exacto: ni www ni localhost). Cualquier campo fuera del contrato se rechaza (400).
   - /api/leads: firstName, lastName, email (obligatorio), phone, company, message,
     origin (≤60), campaign (≤100), interest («contacto» | «informe_demo»), brand,
     domain, country, industry, competitors y el campo trampa `website`. utm_*,
     gclid, fbclid y referrer se resumen en `origin` (canal) y `campaign`; el
     detalle completo va al dataLayer.
   - /api/newsletter/subscribe: email, lists (allowlist del CRM), locale, consent
     (true), consentVersion, origin y `website`. Doble opt-in: el CRM manda el
     correo de confirmación, que lleva a /newsletter/confirmar/?token=….
   Todos los formularios del sitio van al CRM, sin otro servicio de envío (INI-1081).
───────────────────────────────────────────────────────────────────────────── */

// Host del CRM en producción. NEXT_PUBLIC_CRM_URL solo sirve para apuntar a otro CRM en pruebas.
export const CRM_URL = (process.env.NEXT_PUBLIC_CRM_URL || 'https://crm.bisonagents.com').replace(/\/$/, '');
export const CRM_LEADS_URL = `${CRM_URL}/api/leads`;
export const CRM_NEWSLETTER_URL = `${CRM_URL}/api/newsletter`;

// Versión del texto de consentimiento del newsletter que acepta el CRM (el texto lo guarda el CRM por idioma).
export const NEWSLETTER_CONSENT_VERSION = 'consent-v1';
export const NEWSLETTER_LOCALES = ['es', 'en', 'pt-BR'];

const STORAGE_KEY = 'bison_attribution';
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
export const CLICK_ID_KEYS = ['gclid', 'fbclid'];
const MAX_VALUE = 200;

// Límites del contrato del CRM.
const LIMITS = { firstName: 80, lastName: 80, company: 120, message: 2000, origin: 60, campaign: 100 };

const clip = (value, max) => String(value ?? '').trim().slice(0, max);

/** Atribución que trae una URL de llegada: utm_*, gclid, fbclid y el referrer externo (solo el host). */
export function attributionFromUrl(search = '', referrer = '', siteHost = 'bisonagents.com') {
  const params = new URLSearchParams(search);
  const out = {};
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
    const value = clip(params.get(key), MAX_VALUE);
    if (value) out[key] = value;
  }
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.replace(/^www\./, '');
      if (host && host !== siteHost.replace(/^www\./, '')) out.referrer = host;
    } catch {
      /* referrer inválido: se ignora */
    }
  }
  return out;
}

const hasCampaign = (a) => [...UTM_KEYS, ...CLICK_ID_KEYS].some((k) => a?.[k]);

/**
 * Mezcla lo guardado con lo recién leído, dentro de la sesión: una llegada con
 * campaña (utm_* o click id) reemplaza a la anterior; una sin campaña conserva
 * la guardada; y si no había nada, queda el referrer (o vacío).
 */
export function mergeAttribution(previous, next) {
  if (hasCampaign(next)) return { ...next, ...(next.referrer ? {} : previous?.referrer ? { referrer: previous.referrer } : {}) };
  if (previous && Object.keys(previous).length) return previous;
  return next || {};
}

/** Lee la atribución de la sesión (o {}). */
export function getAttribution() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Se llama al cargar el sitio: lee la URL y el referrer, mezcla con lo guardado y lo guarda. */
export function captureAttribution() {
  if (typeof window === 'undefined') return {};
  const merged = mergeAttribution(
    getAttribution(),
    attributionFromUrl(window.location.search, document.referrer, window.location.hostname),
  );
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* almacenamiento bloqueado: la atribución de esta visita se pierde, el formulario sigue */
  }
  return merged;
}

const slug = (value) => String(value || '').toLowerCase().replace(/\s+/g, '-');

/** Canal y campaña para el CRM: `origin` = «web:<fuente>/<medio>», `campaign` = utm_campaign. */
export function crmChannel(attr = {}) {
  const source = slug(attr.utm_source) || (attr.gclid ? 'google' : attr.fbclid ? 'meta' : slug(attr.referrer) || 'directo');
  const medium =
    slug(attr.utm_medium) || (attr.gclid ? 'cpc' : attr.fbclid ? 'social' : attr.referrer ? 'referral' : 'none');
  return {
    origin: `web:${source}/${medium}`.slice(0, LIMITS.origin),
    campaign: clip(attr.utm_campaign, LIMITS.campaign),
  };
}

/** Payload para POST /api/leads a partir del formulario de /contacto. Solo campos del contrato. */
export function buildCrmLead(form, attr = {}) {
  const parts = String(form.name || '').trim().split(/\s+/).filter(Boolean);
  const detalle = [
    form.role && `Cargo: ${form.role}`,
    form.url && `Sitio web: ${form.url}`,
    form.country && `País: ${form.country}`,
    form.archetypeLabel && `Tipo de empresa: ${form.archetypeLabel}`,
    form.planLabel && `Plan de interés: ${form.planLabel}`,
    form.msg && `Mensaje: ${form.msg}`,
  ].filter(Boolean);
  return {
    firstName: clip(parts[0], LIMITS.firstName),
    lastName: clip(parts.slice(1).join(' '), LIMITS.lastName),
    email: clip(form.email, 254),
    company: clip(form.company, LIMITS.company),
    message: clip(detalle.join('\n'), LIMITS.message),
    ...crmChannel(attr),
    interest: 'contacto',
    website: clip(form.botcheck, 200), // campo trampa del CRM: llega vacío si quien escribe es una persona
  };
}

/** Payload para POST /api/newsletter/subscribe. `lists` ya resueltas (blog, rankings-chile…). */
export function buildNewsletterSubscription({ email, lists, locale, origin, botcheck }) {
  return {
    email: clip(email, 254),
    lists: [...new Set(lists)],
    locale: NEWSLETTER_LOCALES.includes(locale) ? locale : 'es',
    consent: true,
    consentVersion: NEWSLETTER_CONSENT_VERSION,
    origin: clip(origin || 'web', LIMITS.origin),
    website: clip(botcheck, 200),
  };
}

/** POST JSON al CRM. Nunca lanza: true si respondió { ok: true }, false si falló (red, CORS, 4xx/5xx). */
async function postCrm(url, payload) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      signal: controller.signal,
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    return res.ok && data.ok === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export const sendLeadToCrm = (payload) => postCrm(CRM_LEADS_URL, payload);
export const sendNewsletterSubscription = (payload) => postCrm(`${CRM_NEWSLETTER_URL}/subscribe`, payload);
/** Confirmar o dar de baja con el token del correo. action: 'confirm' | 'unsubscribe'. */
export const sendNewsletterToken = (action, token) => postCrm(`${CRM_NEWSLETTER_URL}/${action}`, { token });
